import { Box, Slider, Switch, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useRange } from 'react-instantsearch';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import PackageIcon from '../../../assets/icons/package.svg?react';
import useRefine from '../../../hooks/useRefine';
import { currentContextAtom } from '../../../state';
import FilterDisplay, { FilterDisplayMode } from '../../../search/filters/FilterDisplay';
import FilterUtils, { toggleUiStateFilter } from '../../../search/filters/filter.utils';
import { DISPLAYED_MAX_MOV_RANGE, DEFAULT_MAX_MOV_RANGE } from '../../state';

export interface CompanyDeliveryMovFilterProps {
  mode: FilterDisplayMode;
}

export default function CompanyDeliveryMovFilter({ mode }: CompanyDeliveryMovFilterProps): React.JSX.Element {
  const { t } = useTranslation(['discovery']);
  const { companyAddress } = useAtomValue(currentContextAtom);
  const { refine, uiState } = useRefine();
  const [filterDeliveryZones, setFilterDeliveryZones] = useState<boolean>(false);
  const { refine: refineMinimumOrderValue, start } = useRange({
    attribute: 'max_mov',
    min: 0,
    max: DEFAULT_MAX_MOV_RANGE,
  });
  const [sliderValue, setSliderValue] = useState(DISPLAYED_MAX_MOV_RANGE);
  const minimumOrderValue = start[1] === Infinity || start[1] === DEFAULT_MAX_MOV_RANGE ? DISPLAYED_MAX_MOV_RANGE : start[1] as number;

  const deliveryZoneFilter = ` AND ${FilterUtils.GetSupplierCanDeliverFilter(companyAddress.administrativeAreaLevel2!)}`;

  const activeItemCount = useMemo((): number => {
    let value = 0;

    if (minimumOrderValue < DISPLAYED_MAX_MOV_RANGE) {
      value++;
    }

    if (filterDeliveryZones) {
      value++;
    }

    return value;
  }, [minimumOrderValue, filterDeliveryZones]);

  const handleFilterDeliveryZonesChange = (): void => {
    refine({
      filters: toggleUiStateFilter(uiState.filters!, deliveryZoneFilter),
    });
  };

  const handleMinimumOrderValueChange = (value: number): void => {
    if (value === DISPLAYED_MAX_MOV_RANGE) {
      refineMinimumOrderValue([undefined, undefined]);
    } else {
      refineMinimumOrderValue([1, value]);
    }
  };

  useEffect((): void => {
    setSliderValue(minimumOrderValue);
  }, [minimumOrderValue]);

  useEffect((): void => {
    setFilterDeliveryZones(!!uiState.filters?.includes(deliveryZoneFilter));
  }, [uiState.filters]);

  useEffect((): void => {
    refineMinimumOrderValue([undefined, undefined]);
  }, []);

  return (
    <FilterDisplay
      label={t('discovery:mov_and_delivery')}
      icon={<PackageIcon style={{ fill: 'currentColor' }} />}
      name="delivery_mov"
      activeItemCount={activeItemCount}
      content={(
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="titleMedium">
                {t('discovery:mov_amount')}
              </Typography>
            </Box>
            <Typography variant="labelLarge" sx={{ my: 1 }}>
              {t('discovery:minimum_order_value_range', { value: minimumOrderValue })} {minimumOrderValue === DISPLAYED_MAX_MOV_RANGE && t('common:and_more')}
            </Typography>
            <Box sx={{ px: 1 }}>
              <Slider
                max={DISPLAYED_MAX_MOV_RANGE}
                value={sliderValue}
                onChange={(event, value) => handleMinimumOrderValueChange(value)}
              />
            </Box>
          </Box>
          <Box>
            <Typography variant="titleMedium">
              {t('common:delivery')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
              <Typography variant="bodyLarge">
                {t('discovery:only_suppliers_can_deliver')}
              </Typography>
              <Switch checked={filterDeliveryZones} onChange={handleFilterDeliveryZonesChange} />
            </Box>
          </Box>
        </Box>
      )}
      mode={mode}
    />
  );
}
